import { DEG, clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import type { RenderTarget, TechniqueContext } from '../types';
import type { ErosionValues } from './params';

/**
 * Falling-sand automaton over the text.
 *
 * Grains fall along the chosen direction and come to rest when blocked, sliding
 * diagonally with probability `spread` — that single rule is what produces a natural
 * angle of repose, so the piles that build on the letterforms have a believable slope
 * instead of stacking into columns.
 *
 * The grid is direction-agnostic: rather than special-casing "down", the fall vector is
 * rotated and grains step along it, so the same code produces sand blowing sideways.
 */
export interface ErosionState {
  /** Occupancy grid; 0 empty, 1 sand, 2 the text itself. */
  grid: Uint8Array;
  /** Stack height at rest and the generation it settled in, for colouring. */
  height: Uint16Array;
  settledAt: Uint16Array;
  cols: number;
  rows: number;
  cell: number;
  placed: number;
  emitted: number;
  palette: ResolvedPalette;
  maxHeight: number;
  targetGrains: number;
  done: boolean;
}

function values(ctx: TechniqueContext): ErosionValues {
  return ctx.values as unknown as ErosionValues;
}

export function init(ctx: TechniqueContext): ErosionState {
  const v = values(ctx);
  const cell = Math.max(1, v.cell);
  const cols = Math.max(1, Math.ceil(ctx.width / cell));
  const rows = Math.max(1, Math.ceil(ctx.height / cell));
  const grid = new Uint8Array(cols * rows);

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (ctx.field.coverage((c + 0.5) * cell, (r + 0.5) * cell) > 0.5) grid[r * cols + c] = 2;
    }
  }

  return {
    grid,
    height: new Uint16Array(cols * rows),
    settledAt: new Uint16Array(cols * rows),
    cols,
    rows,
    cell,
    placed: 0,
    emitted: 0,
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    maxHeight: 1,
    targetGrains: v.grains,
    done: false,
  };
}

export function step(state: ErosionState, _dt: number, ctx: TechniqueContext): ErosionState {
  if (state.done) return state;
  const v = values(ctx);

  const angle = v.direction * DEG;
  const fx = Math.cos(angle);
  const fy = Math.sin(angle);
  // Perpendicular, for the sideways slide that creates the angle of repose.
  const px = -fy;
  const py = fx;

  const { cols, rows, grid } = state;
  const budget = Math.max(200, Math.round(6000 * clamp01(ctx.quality)));

  for (let n = 0; n < budget; n++) {
    if (state.emitted >= state.targetGrains) {
      state.done = true;
      break;
    }
    state.emitted++;

    // Emit from the upwind edge of the canvas.
    let gx: number;
    let gy: number;
    if (Math.abs(fy) >= Math.abs(fx)) {
      gx = ctx.rng.int(0, cols - 1);
      gy = fy > 0 ? 0 : rows - 1;
    } else {
      gy = ctx.rng.int(0, rows - 1);
      gx = fx > 0 ? 0 : cols - 1;
    }

    if (v.mode === 'erode' && grid[gy * cols + gx] !== 0) continue;

    for (let fall = 0; fall < v.maxFall; fall++) {
      const nx = Math.round(gx + fx);
      const ny = Math.round(gy + fy);

      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) {
        // Left the canvas without settling.
        gx = -1;
        break;
      }

      const ahead = grid[ny * cols + nx];
      if (ahead === 0) {
        gx = nx;
        gy = ny;
        continue;
      }

      if (v.mode === 'erode' && ahead === 2) {
        // Erosion: the grain knocks the text cell out instead of resting on it.
        grid[ny * cols + nx] = 0;
        gx = -1;
        break;
      }

      // Blocked: try to slide to one side, otherwise settle here.
      const side = ctx.rng.chance(0.5) ? 1 : -1;
      let slid = false;
      if (ctx.rng.chance(v.spread)) {
        for (const s of [side, -side]) {
          const sx = Math.round(gx + fx + px * s);
          const sy = Math.round(gy + fy + py * s);
          if (sx < 0 || sy < 0 || sx >= cols || sy >= rows) continue;
          if (grid[sy * cols + sx] !== 0) continue;
          gx = sx;
          gy = sy;
          slid = true;
          break;
        }
      }
      if (slid) continue;
      if (!ctx.rng.chance(v.stickiness)) continue;
      break;
    }

    if (gx < 0) continue;
    const i = gy * cols + gx;
    if (grid[i] !== 0) continue;

    grid[i] = 1;
    state.placed++;

    // Stack height: how deep the pile *beneath* this grain is, counted downwind.
    //
    // Counting upwind would always return 1 — a grain settles on top of the pile, so
    // nothing has landed above it yet. Downwind is what is already there, and it is
    // final the moment the grain comes to rest.
    let h = 1;
    for (let k = 1; k < 400; k++) {
      const bx = Math.round(gx + fx * k);
      const by = Math.round(gy + fy * k);
      if (bx < 0 || by < 0 || bx >= cols || by >= rows) break;
      if (grid[by * cols + bx] === 0) break;
      h++;
    }
    state.height[i] = h;
    if (h > state.maxHeight) state.maxHeight = h;
    state.settledAt[i] = Math.min(65535, state.placed);
  }

  return state;
}

export function isComplete(state: ErosionState): boolean {
  return state.done;
}

export function draw(state: ErosionState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  const buckets = new Map<string, Path2D>();

  for (let r = 0; r < state.rows; r++) {
    for (let col = 0; col < state.cols; col++) {
      const i = r * state.cols + col;
      if (state.grid[i] !== 1) continue;

      let t: number;
      switch (v.colorMode) {
        case 'height':
          t = clamp01(state.height[i] / state.maxHeight);
          break;
        case 'time':
          t = clamp01(state.settledAt[i] / Math.max(1, state.placed));
          break;
        default:
          t = 0;
      }
      const color = v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(t, v.opacity);
      let path = buckets.get(color);
      if (!path) {
        path = new Path2D();
        buckets.set(color, path);
      }
      path.rect(col * state.cell, r * state.cell, state.cell, state.cell);
    }
  }

  for (const [color, path] of buckets) {
    c.fillStyle = color;
    c.fill(path);
  }
}

export function elementCount(state: ErosionState): number {
  return state.placed;
}
